import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { JobDetailDto, MaterialDto, WorkCenterDto } from "@grizzly/shared";
import { jobStatus, taskPriority } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { ErrorState, Skeleton } from "../components/ui/states.js";
import { JobActionBar } from "../components/jobs/jobActionBar.js";
import { OperationPanel } from "../components/jobs/operationPanel.js";
import { MaterialPanel } from "../components/jobs/materialPanel.js";
import { IssuePanel } from "../components/jobs/issuePanel.js";
import { HistoryPanel } from "../components/jobs/historyPanel.js";
import { JobDocumentPanel } from "../components/jobs/documentPanel.js";
import { ArrowLeft } from "lucide-react";

export function JobDetailPage() {
  const { id = "" } = useParams();
  const { t } = useI18n();
  const [job, setJob] = useState<JobDetailDto | null>(null);
  const [error, setError] = useState<{ message: string } | null>(null);
  const [workCenters, setWorkCenters] = useState<WorkCenterDto[]>([]);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await api.getJob(id);
      setJob(res.job);
      setError(null);
    } catch (err) {
      setError({ message: (err as ApiClientError).message });
    }
  }, [id]);

  useEffect(() => {
    void load();
    api
      .listWorkCenters()
      .then((r) => setWorkCenters(r.items))
      .catch(() => undefined);
    api
      .listMaterials()
      .then((r) => setMaterials(r.items))
      .catch(() => undefined);
  }, [load]);

  if (error || !job) return <ErrorState message={error?.message ?? t("jobs.notFound")} />;
  if (!job.id) return <Skeleton rows={6} />;

  const canEditOps = job.status === "planned";

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/jobs">
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />{" "}
            {t("jobs.header.back")}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={`${job.number} — ${job.title}`}
        description={job.customerName ?? t("jobs.internalWork")}
        actions={<JobActionBar job={job} onChanged={() => void load()} />}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
        <Badge tone={jobStatus(job.status).tone}>
          {t(`status.job.${job.status}` as MessageKey)}
        </Badge>
        <Badge tone={taskPriority(job.priority).tone}>
          {t(`status.priority.${job.priority}` as MessageKey)}
        </Badge>
        <span className="tabular">
          {t("jobs.meta.quantity", { quantity: job.quantity })}
        </span>
        {job.dueDate ? <span>{t("jobs.meta.due", { date: job.dueDate })}</span> : null}
      </div>
      {job.description ? (
        <p className="mb-4 max-w-prose text-sm text-ink-muted">{job.description}</p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <OperationPanel
            jobId={job.id}
            operations={job.operations}
            workCenters={workCenters}
            canEdit={canEditOps}
            onChanged={() => void load()}
          />
        </Card>
        <Card>
          <MaterialPanel
            jobId={job.id}
            materials={job.materials}
            catalog={materials}
            canEdit={canEditOps}
            onChanged={() => void load()}
          />
        </Card>
        <Card>
          <IssuePanel jobId={job.id} issues={job.issues} onChanged={() => void load()} />
        </Card>
        <Card>
          <HistoryPanel history={job.history} />
        </Card>
        <div className="xl:col-span-2">
          <JobDocumentPanel jobId={job.id} />
        </div>
      </div>
    </>
  );
}
